import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { MarketingService, MarketingImageService } from '@/services/MarketingService'
import { marketingKeys } from './queryKeys'
import type { VenueMarketingPageDto } from '@lib/dto/venue-marketing-page.dto'
import type { VenueMarketingContent } from '@lib/dto/venue-marketing.dto'

/**
 * Every marketing page a venue has: its own, and one per track that has one.
 * `enabled` is for callers outside the marketing section, which must not ask an
 * organization without the feature (the API refuses it).
 */
export function useVenueMarketing(venueId: string, enabled = true): UseQueryResult<VenueMarketingPageDto[]> {
  return useQuery({
    queryKey: marketingKeys.forVenue(venueId),
    queryFn: () => MarketingService.list(venueId),
    enabled,
  })
}

/** What a save needs: which page, and the document to put on it. */
interface SaveMarketingPage {
  venueId: string
  eventTrackId: string | null
  content: VenueMarketingContent
}

/**
 * Save one page.
 *
 * Invalidates the venue's pages rather than writing the response into the cache:
 * a save can create a page that did not exist, and the server decides its id.
 */
export function useSaveVenueMarketing() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ venueId, eventTrackId, content }: SaveMarketingPage) =>
      MarketingService.save(venueId, eventTrackId, content),
    onSuccess: (_result, { venueId }) => {
      void queryClient.invalidateQueries({ queryKey: marketingKeys.forVenue(venueId) })
    },
  })
}

/** Every image this venue has uploaded, for the picker. */
export function useMarketingImages(venueId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...marketingKeys.forVenue(venueId), 'images'],
    queryFn: () => MarketingImageService.list(venueId),
    enabled,
  })
}

/** Upload one image and make it available to the picker straight away. */
export function useUploadMarketingImage(venueId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (dataUrl: string) => MarketingImageService.upload(venueId, dataUrl),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...marketingKeys.forVenue(venueId), 'images'],
      })
    },
  })
}
